import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    SignOut,
    ArrowsClockwise,
    Phone,
    MagnifyingGlass,
    Download,
    Trash,
    Crown,
    WhatsappLogo,
    Confetti,
    X,
    VideoCamera,
    FloppyDisk,
    YoutubeLogo,
    TiktokLogo,
    FacebookLogo,
} from "@phosphor-icons/react";
import { GIVEAWAY_MESSAGE } from "../lib/phone";
import { parseVideoUrl } from "../lib/video";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINNER_MESSAGE =
    "\uD83C\uDF89 CONGRATULATIONS! YOU HAVE WON $100,000 TO SHOP BACK ON IOS.MATRIXSHOPPING.COM";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function AdminEntriesPage() {
    const nav = useNavigate();
    const [token] = useState(() => localStorage.getItem("admin_token") || "");
    const [username] = useState(
        () => localStorage.getItem("admin_username") || "admin"
    );
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");

    useEffect(() => {
        if (!token) nav("/admin", { replace: true });
    }, [token, nav]);

    const load = useCallback(
        async (query = "") => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/admin/entries`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: query ? { q: query } : {},
                    timeout: 20000,
                });
                setEntries(data.entries || []);
                setTotal(data.total || 0);
            } catch (err) {
                if (err.response?.status === 401) {
                    localStorage.removeItem("admin_token");
                    localStorage.removeItem("admin_username");
                    nav("/admin", { replace: true });
                    return;
                }
                toast.error("Could not load entries.");
            } finally {
                setLoading(false);
            }
        },
        [token, nav]
    );

    useEffect(() => {
        if (token) load();
    }, [token, load]);

    const signOut = () => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_username");
        nav("/admin", { replace: true });
    };

    const progress = useMemo(() => Math.min(100, (total / 1000) * 100), [total]);

    const exportCsv = () => {
        const rows = [
            ["Phone", "Source", "Created At"],
            ...entries.map((e) => [e.phone, e.source, e.created_at]),
        ];
        const csv = rows
            .map((r) =>
                r
                    .map((c) => {
                        const s = String(c ?? "");
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    })
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `entries-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const clearAll = async () => {
        if (
            !window.confirm(
                `Delete ALL ${total} entries? This cannot be undone.`
            )
        )
            return;
        try {
            const { data } = await axios.delete(`${API}/admin/entries`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            });
            toast.success(`Cleared ${data.deleted} entries.`);
            load(q);
        } catch {
            toast.error("Could not clear entries.");
        }
    };

    const deleteOne = async (id, phone) => {
        if (!window.confirm(`Delete entry +${phone}?`)) return;
        try {
            await axios.delete(`${API}/admin/entries/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 15000,
            });
            toast.success("Entry deleted");
            load(q);
        } catch {
            toast.error("Could not delete entry.");
        }
    };

    // ---------- Winner ----------
    const [winner, setWinner] = useState(null);
    const [picking, setPicking] = useState(false);
    const goalReached = total >= 1000;

    const pickWinner = async () => {
        if (total === 0) {
            toast.error("No entries to pick from.");
            return;
        }
        if (
            !goalReached &&
            !window.confirm(
                `Only ${total} entries so far (goal is 1,000). Pick a TEST winner anyway?`
            )
        )
            return;
        setPicking(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/pick-winner`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 15000,
                }
            );
            setWinner({ ...data, isTest: !data.goal_reached });
        } catch {
            toast.error("Could not pick a winner.");
        } finally {
            setPicking(false);
        }
    };

    // ---------- Featured video widget ----------
    const [videoUrl, setVideoUrl] = useState("");
    const [videoSaving, setVideoSaving] = useState(false);
    const videoEmbed = useMemo(() => parseVideoUrl(videoUrl), [videoUrl]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/settings/video`, {
                    timeout: 10000,
                });
                setVideoUrl(data.url || "");
            } catch {
                // ignore
            }
        })();
    }, []);

    const saveVideo = async (nextUrl) => {
        setVideoSaving(true);
        try {
            const { data } = await axios.put(
                `${API}/settings/video`,
                { url: nextUrl ?? "" },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 15000,
                }
            );
            setVideoUrl(data.url || "");
            toast.success(
                (data.url ? "Featured video updated." : "Featured video cleared.")
            );
        } catch (err) {
            const msg =
                err.response?.data?.detail || err.message || "Could not save video.";
            toast.error(typeof msg === "string" ? msg : "Save failed.");
        } finally {
            setVideoSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F4F4F5]">
            {/* Header */}
            <header className="border-b-2 border-zinc-900 bg-white">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                            Admin dashboard
                        </div>
                        <div className="font-display text-2xl font-extrabold tracking-tight">
                            Giveaway entries
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                            @{username}
                        </div>
                        <button
                            type="button"
                            onClick={signOut}
                            data-testid="admin-signout"
                            className="border-2 border-zinc-900 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-zinc-900 hover:text-white transition-colors inline-flex items-center gap-2"
                        >
                            <SignOut size={12} weight="bold" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <div className="border-2 border-zinc-900 bg-[#FFCC00] p-5">
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold">
                            Total entries
                        </div>
                        <div
                            className="font-display text-5xl font-extrabold tracking-tighter mt-1"
                            data-testid="admin-total-entries"
                        >
                            {total.toLocaleString()}
                        </div>
                    </div>
                    <div className="border-2 border-zinc-900 bg-white p-5">
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                            Goal
                        </div>
                        <div className="font-display text-5xl font-extrabold tracking-tighter mt-1">
                            1,000
                        </div>
                    </div>
                    <div className="border-2 border-zinc-900 bg-white p-5">
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-2">
                            Progress
                        </div>
                        <div className="w-full h-4 border-2 border-zinc-900 bg-white">
                            <div
                                className="h-full bg-[#16A34A]"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <div className="mt-2 font-mono-tab text-sm font-bold">
                            {progress.toFixed(1)}%
                        </div>
                    </div>
                </div>

                {/* Pick winner */}
                <div
                    className={`border-2 border-zinc-900 mb-8 p-5 shadow-brutal ${
                        goalReached ? "bg-[#FFCC00]" : "bg-white"
                    }`}
                >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                        <div className="flex items-center gap-3">
                            <div
                                className={`w-12 h-12 border-2 border-zinc-900 flex items-center justify-center ${
                                    goalReached ? "bg-zinc-900" : "bg-[#FFCC00]"
                                }`}
                            >
                                <Crown
                                    size={22}
                                    weight="fill"
                                    color={goalReached ? "#FFCC00" : "#09090B"}
                                />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] font-bold">
                                    {goalReached
                                        ? "Goal reached — draw the winner!"
                                        : "Draw a random winner"}
                                </div>
                                <div className="font-display text-2xl font-extrabold tracking-tight">
                                    $100,000 GIVEAWAY
                                </div>
                                {!goalReached && (
                                    <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500 mt-1">
                                        {total} / 1,000 entries · test mode available
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={pickWinner}
                            disabled={picking || total === 0}
                            data-testid="admin-pick-winner"
                            className={`border-2 border-zinc-900 px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ${
                                goalReached
                                    ? "bg-zinc-900 text-[#FFCC00] hover:bg-white hover:text-zinc-900"
                                    : "bg-zinc-900 text-white hover:bg-[#FFCC00] hover:text-zinc-900"
                            }`}
                        >
                            {picking ? (
                                <>
                                    <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    Drawing…
                                </>
                            ) : (
                                <>
                                    <Crown size={14} weight="fill" />
                                    {goalReached ? "PICK WINNER" : "TEST PICK WINNER"}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Featured video widget */}
                <div className="border-2 border-zinc-900 bg-white mb-8 shadow-brutal">
                    <div className="border-b-2 border-zinc-900 bg-zinc-900 text-white px-5 py-3 flex items-center gap-2 justify-between">
                        <div className="flex items-center gap-2">
                            <VideoCamera size={16} weight="fill" color="#FFCC00" />
                            <span className="text-[11px] uppercase tracking-[0.25em] font-extrabold">
                                Featured video · shown on the homepage
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">
                            <YoutubeLogo size={14} weight="bold" />
                            <TiktokLogo size={14} weight="bold" />
                            <FacebookLogo size={14} weight="bold" />
                        </div>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-5">
                        <div className="md:col-span-7">
                            <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">
                                Paste a YouTube, TikTok or Facebook link
                            </label>
                            <input
                                type="url"
                                data-testid="admin-video-url"
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=… · https://www.tiktok.com/@user/video/… · https://www.facebook.com/watch?v=…"
                                className="w-full bg-white border-2 border-zinc-900 px-3 py-3 text-sm font-mono-tab text-zinc-900 focus:outline-none focus:shadow-brutal-sm rounded-none placeholder:text-zinc-300"
                            />
                            {videoUrl && !videoEmbed && (
                                <div className="mt-2 text-[11px] uppercase tracking-[0.15em] font-bold text-red-700">
                                    Unrecognised URL — must be YouTube, TikTok or Facebook.
                                </div>
                            )}
                            {videoEmbed && (
                                <div className="mt-2 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                                    Detected · <span className="text-zinc-900">{videoEmbed.platform}</span>{" "}
                                    · aspect {videoEmbed.aspect}
                                </div>
                            )}

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => saveVideo(videoUrl)}
                                    disabled={videoSaving || (videoUrl && !videoEmbed)}
                                    data-testid="admin-video-save"
                                    className="border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                                >
                                    <FloppyDisk size={13} weight="bold" />
                                    {videoSaving ? "Saving…" : "Save & publish"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setVideoUrl("");
                                        saveVideo("");
                                    }}
                                    disabled={videoSaving}
                                    data-testid="admin-video-clear"
                                    className="border-2 border-zinc-900 bg-white text-zinc-900 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                    <Trash size={13} weight="bold" />
                                    Clear
                                </button>
                            </div>
                            <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
                                Supported: YouTube (watch / shorts / youtu.be), TikTok (video links), Facebook (watch / reel).
                                Changes appear on the homepage instantly.
                            </p>
                        </div>

                        <div className="md:col-span-5">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">
                                Preview
                            </div>
                            <div
                                className="w-full border-2 border-zinc-900 bg-zinc-100 relative overflow-hidden"
                                style={{
                                    aspectRatio: videoEmbed?.aspect || "16/9",
                                    maxWidth: videoEmbed?.aspect === "9/16" ? "240px" : "100%",
                                }}
                            >
                                {videoEmbed ? (
                                    <iframe
                                        key={videoEmbed.embedUrl}
                                        title="Video preview"
                                        src={videoEmbed.embedUrl}
                                        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                                        allowFullScreen
                                        className="absolute inset-0 w-full h-full border-0"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 text-center px-4">
                                        Paste a link above
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") load(q);
                            }}
                            placeholder="Search by phone digits …"
                            data-testid="admin-search"
                            className="w-full bg-white border-2 border-zinc-900 px-4 py-3 pl-10 text-sm font-mono-tab focus:outline-none focus:shadow-brutal-sm rounded-none"
                        />
                        <MagnifyingGlass
                            size={14}
                            weight="bold"
                            className="absolute left-3 top-1/2 -translate-y-1/2"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => load(q)}
                        className="border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors inline-flex items-center gap-2"
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={exportCsv}
                        className="border-2 border-zinc-900 bg-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] transition-colors inline-flex items-center gap-2"
                    >
                        <Download size={14} weight="bold" />
                        Export CSV
                    </button>
                    <button
                        type="button"
                        onClick={clearAll}
                        data-testid="admin-clear-all"
                        className="border-2 border-zinc-900 bg-white text-red-700 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors inline-flex items-center gap-2"
                    >
                        <Trash size={14} weight="bold" />
                        Clear all
                    </button>
                </div>

                {/* Table */}
                <div className="border-2 border-zinc-900 bg-white overflow-hidden">
                    <div className="grid grid-cols-12 bg-zinc-900 text-white font-bold uppercase tracking-[0.2em] text-[11px]">
                        <div className="col-span-1 px-4 py-3">#</div>
                        <div className="col-span-5 px-4 py-3">Phone</div>
                        <div className="col-span-2 px-4 py-3">Source</div>
                        <div className="col-span-3 px-4 py-3">Received</div>
                        <div className="col-span-1 px-4 py-3 text-right">·</div>
                    </div>
                    {loading ? (
                        <div className="p-6 text-center text-sm font-bold text-zinc-500">
                            Loading…
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="p-12 text-center text-sm text-zinc-500">
                            No entries yet.
                        </div>
                    ) : (
                        entries.map((e, i) => (
                            <div
                                key={e.id}
                                className={`grid grid-cols-12 items-center font-mono-tab text-sm ${
                                    i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"
                                } border-t border-zinc-200`}
                                data-testid={`entry-row-${i}`}
                            >
                                <div className="col-span-1 px-4 py-3 text-zinc-500">
                                    {i + 1}
                                </div>
                                <div className="col-span-5 px-4 py-3 font-bold flex items-center gap-2">
                                    <Phone size={12} weight="bold" />
                                    <a
                                        href={`https://wa.me/${e.phone}?text=${encodeURIComponent(
                                            GIVEAWAY_MESSAGE
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-[#25D366]"
                                        title="Open WhatsApp with the giveaway message"
                                    >
                                        +{e.phone}
                                    </a>
                                </div>
                                <div className="col-span-2 px-4 py-3 text-zinc-500 uppercase tracking-widest text-[10px] font-bold">
                                    {e.source}
                                </div>
                                <div className="col-span-3 px-4 py-3 text-zinc-500">
                                    {fmtDate(e.created_at)}
                                </div>
                                <div className="col-span-1 px-4 py-3 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => deleteOne(e.id, e.phone)}
                                        aria-label="Delete entry"
                                        className="w-7 h-7 flex items-center justify-center border-2 border-zinc-900 bg-white hover:bg-red-700 hover:text-white transition-colors"
                                    >
                                        <Trash size={11} weight="bold" />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </main>

            {/* Winner modal */}
            {winner && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
                    onClick={() => setWinner(null)}
                    data-testid="winner-modal"
                >
                    <div
                        className="w-full max-w-lg border-2 border-zinc-900 bg-white shadow-brutal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-[#FFCC00] border-b-2 border-zinc-900 px-6 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Confetti size={18} weight="fill" />
                                <span className="text-[11px] uppercase tracking-[0.25em] font-extrabold">
                                    {winner.isTest ? "Test draw" : "WE HAVE A WINNER!"}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setWinner(null)}
                                className="w-6 h-6 flex items-center justify-center border-2 border-zinc-900 bg-white hover:bg-zinc-900 hover:text-[#FFCC00]"
                                aria-label="Close"
                            >
                                <X size={11} weight="bold" />
                            </button>
                        </div>

                        <div className="p-6">
                            {winner.isTest && (
                                <div className="border-2 border-dashed border-zinc-400 bg-[#FFFDF2] px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-700 mb-4">
                                    ⚠ Preview only · {total} / 1,000 entries — not a live draw
                                </div>
                            )}

                            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                Winner
                            </div>
                            <h3 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-1">
                                $100,000 GYD
                            </h3>
                            <div className="mt-3 border-2 border-zinc-900 bg-white p-4 flex items-center gap-3">
                                <Crown size={26} weight="fill" color="#FFCC00" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                                        Phone number
                                    </div>
                                    <div
                                        className="font-mono-tab text-2xl font-extrabold tracking-tight"
                                        data-testid="winner-phone"
                                    >
                                        +{winner.phone}
                                    </div>
                                </div>
                            </div>

                            <a
                                href={`https://wa.me/${winner.phone}?text=${encodeURIComponent(
                                    WINNER_MESSAGE
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="winner-whatsapp-btn"
                                className="mt-5 w-full border-2 border-zinc-900 bg-[#25D366] text-white px-4 py-4 text-sm font-extrabold uppercase tracking-[0.2em] hover:-translate-y-0.5 hover:shadow-brutal transition-all inline-flex items-center justify-center gap-2 shadow-brutal-sm"
                            >
                                <WhatsappLogo size={22} weight="fill" />
                                $100,000 WINNER →
                            </a>

                            <div className="mt-4 text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                                Message that will be sent
                            </div>
                            <div className="mt-1 border-2 border-dashed border-zinc-300 bg-[#F8FAFC] p-3 text-xs text-zinc-700 leading-relaxed">
                                {WINNER_MESSAGE}
                            </div>

                            <div className="mt-5 flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setWinner(null)}
                                    className="flex-1 border-2 border-zinc-900 bg-white text-zinc-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={pickWinner}
                                    disabled={picking}
                                    className="flex-1 border-2 border-zinc-900 bg-zinc-900 text-white px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-50"
                                >
                                    Re-draw
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
